/**
 * Custom Pack order sheet.
 *
 * One page where a buyer picks colours and quantities (for example 10 kg in 10 colours) and adds all of it to the cart
 * together. The sheet keeps its own list of numbers, tells the shared "Mix and save" bar its total, shows totals and a
 * preview of the discount, and adds every chosen colour with one request to /cart/add.js.
 *
 * Launch state: the server renders the steppers and the button as disabled, so nothing here can add to the cart.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MAX_PER_ROW = 99;

class SureSpoolCustomPack extends HTMLElement {
  /** @type {AbortController | null} */
  abort = null;
  /** @type {{ qty: number, price: number }[]} */
  milestones = [];
  busy = false;
  /** @type {Intl.NumberFormat | null} */
  money = null;

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    try {
      this.milestones = JSON.parse(this.dataset.milestones || '[]');
    } catch (error) {
      this.milestones = [];
    }
    this.milestones.sort((a, b) => a.qty - b.qty);

    try {
      this.money = new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.dataset.currency || 'INR' });
    } catch (error) {
      this.money = null;
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    this.#update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  get #kgPerSpool() {
    const kg = parseFloat(this.dataset.kgPerSpool || '1');
    return Number.isFinite(kg) && kg > 0 ? kg : 1;
  }

  get #launch() {
    return this.dataset.launch === 'true';
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-pack-row]')]);
  }

  /** The most spools a row can take: its stock (when stock is tracked) or 99. Zero means the row cannot be chosen. */
  #maxFor(/** @type {HTMLElement} */ row) {
    if (this.#launch || row.dataset.available !== 'true') return 0;
    const max = parseInt(row.dataset.max || String(MAX_PER_ROW), 10);
    return Math.min(MAX_PER_ROW, Number.isFinite(max) ? max : MAX_PER_ROW);
  }

  #qty(/** @type {HTMLElement} */ row) {
    const input = row.querySelector('[data-pack-qty]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  #setQty(/** @type {HTMLElement} */ row, /** @type {number} */ value) {
    const input = row.querySelector('[data-pack-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const clamped = Math.max(0, Math.min(this.#maxFor(row), Math.floor(Number(value) || 0)));
    if (input.value !== String(clamped)) input.value = String(clamped);
  }

  #onClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const plus = target.closest('[data-pack-plus]');
    const minus = target.closest('[data-pack-minus]');
    if (plus || minus) {
      const row = /** @type {HTMLElement | null} */ ((plus || minus)?.closest('[data-pack-row]') ?? null);
      if (!row) return;
      this.#setQty(row, this.#qty(row) + (plus ? 1 : -1));
      this.#update();
      return;
    }

    const quick = target.closest('[data-pack-quick]');
    if (quick instanceof HTMLElement) {
      this.#quickStart(parseFloat(quick.dataset.packQuick || '0'));
      return;
    }

    if (target.closest('[data-pack-add]')) {
      event.preventDefault();
      this.#addToCart();
    }
  };

  #onInput = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-pack-qty]')) return;
    const row = /** @type {HTMLElement | null} */ (target.closest('[data-pack-row]'));
    if (!row) return;
    // While typing, keep only digits and the allowed range. An empty box counts as 0.
    if (target.value === '') {
      this.#update();
      return;
    }
    this.#setQty(row, parseInt(target.value, 10));
    this.#update();
  };

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-pack-qty]')) return;
    const row = /** @type {HTMLElement | null} */ (target.closest('[data-pack-row]'));
    if (row) this.#setQty(row, this.#qty(row));
    this.#update();
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-pack-qty]')) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const row = /** @type {HTMLElement | null} */ (target.closest('[data-pack-row]'));
    if (!row) return;
    this.#setQty(row, this.#qty(row) + (event.key === 'ArrowUp' ? 1 : -1));
    this.#update();
  };

  /** Spreads a number of kilograms evenly over the colours that can be chosen. The buyer can change it after. */
  #quickStart(/** @type {number} */ kg) {
    if (this.#launch || !(kg > 0)) return;
    const rows = this.#rows.filter((row) => this.#maxFor(row) > 0);
    if (rows.length === 0) return;
    let left = Math.round(kg / this.#kgPerSpool);
    rows.forEach((row) => this.#setQty(row, 0));
    // Hand out one spool at a time, round and round, so every colour gets its fair share (and stock limits are kept).
    let guard = left * rows.length + 1;
    while (left > 0 && guard-- > 0) {
      for (const row of rows) {
        if (left <= 0) break;
        const now = this.#qty(row);
        if (now < this.#maxFor(row)) {
          this.#setQty(row, now + 1);
          left -= 1;
        }
      }
    }
    this.#update();
  }

  #format(/** @type {number} */ cents) {
    const amount = cents / 100;
    return this.money ? this.money.format(amount) : amount.toFixed(2);
  }

  /** Writes every number on the page, tells the shared bar the total, and sets the button state. */
  #update() {
    let spools = 0;
    let subtotal = 0;
    this.#rows.forEach((row) => {
      const qty = this.#qty(row);
      const max = this.#maxFor(row);
      spools += qty;
      subtotal += qty * (parseInt(row.dataset.price || '0', 10) || 0);

      const minus = row.querySelector('[data-pack-minus]');
      const plus = row.querySelector('[data-pack-plus]');
      const input = row.querySelector('[data-pack-qty]');
      if (minus instanceof HTMLButtonElement) minus.disabled = qty <= 0 || max === 0;
      if (plus instanceof HTMLButtonElement) plus.disabled = qty >= max;
      if (input instanceof HTMLInputElement) {
        input.disabled = max === 0;
        input.max = String(max);
      }
      row.classList.toggle('has-qty', qty > 0);
    });

    // The best price per spool reached so far (rupees, from the theme settings). Shopify applies the matching discount.
    const basePrice = Number(this.dataset.basePrice) || 0;
    let tierPrice = 0;
    for (const m of this.milestones) if (spools >= m.qty) tierPrice = m.price;
    const offEach = tierPrice > 0 ? Math.max(0, basePrice - tierPrice) * 100 : 0;
    const discounted = Math.max(0, subtotal - spools * offEach);

    const kg = spools * this.#kgPerSpool;
    const kgText = Number.isInteger(kg) ? String(kg) : kg.toFixed(1);
    this.#bind('[data-pack-spools]', String(spools));
    this.#bind('[data-pack-kg]', kgText);
    if (!this.#launch) {
      this.#bind('[data-pack-subtotal]', this.#format(subtotal));
      this.#bind('[data-pack-discount]', tierPrice > 0 ? `₹${tierPrice} each + GST` : this.dataset.noneText || '');
      this.#bind('[data-pack-total]', this.#format(discounted));
    }

    const addButtons = this.querySelectorAll('[data-pack-add]');
    addButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = this.#launch || spools <= 0 || this.busy;
    });

    document.dispatchEvent(new CustomEvent('surespool:pack-total', { detail: { total: spools } }));
  }

  #bind(/** @type {string} */ selector, /** @type {string} */ text) {
    this.querySelectorAll(selector).forEach((element) => {
      if (element.textContent !== text) element.textContent = text;
    });
  }

  #message(/** @type {string} */ text) {
    this.querySelectorAll('[data-pack-message]').forEach((element) => {
      element.textContent = text;
    });
  }

  /** Adds every chosen colour with one request, then opens the cart drawer. */
  async #addToCart() {
    if (this.busy || this.#launch) return;
    const items = this.#rows
      .map((row) => ({ id: Number(row.dataset.variantId), quantity: this.#qty(row) }))
      .filter((item) => item.id && item.quantity > 0);
    if (items.length === 0) return;

    this.busy = true;
    this.classList.add('is-busy');
    this.#message('');
    this.#update();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : ''))
        .filter(Boolean)
        .join(',');
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(sectionIds ? { sections: sectionIds } : {}) }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const text = result.description || result.message || this.dataset.errorText || 'Could not add the pack.';
        this.#message(text);
        this.dispatchEvent(new CartErrorEvent({ error: text, code: 'INVALID', detail: { description: result.description, errors: result.errors } }));
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'custom-pack', sourceId: this.id || 'custom-pack', itemCount: 0 },
        });
        return;
      }

      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'custom-pack',
          sourceId: this.id || 'custom-pack',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
          didError: false,
        },
      });

      // The pack is in the cart: clear the sheet and show the cart.
      this.#rows.forEach((row) => this.#setQty(row, 0));
      this.#message(this.dataset.doneText || 'Added to your cart.');
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') /** @type {any} */ (drawer).open();
    } catch (error) {
      const text = this.dataset.errorText || 'Could not add the pack. Please try again.';
      this.#message(text);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: text, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.busy = false;
      this.classList.remove('is-busy');
      this.#update();
    }
  }
}

if (!customElements.get('surespool-custom-pack')) {
  customElements.define('surespool-custom-pack', SureSpoolCustomPack);
}
